import React from 'react';
import { AlertTriangle } from 'lucide-react';
import AnimatedModal from './AnimatedModal';

/**
 * ConfirmModal – app-consistent confirmation dialog built on AnimatedModal.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {() => void} onConfirm
 * @param {string} title
 * @param {string} message
 * @param {string} [confirmLabel='Confirm']
 * @param {string} [cancelLabel='Cancel']
 * @param {'danger' | 'warning' | 'default'} [variant='danger']
 * @param {boolean} [loading=false]
 */
const VARIANT_STYLES = {
  danger: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    confirmBtn: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    confirmBtn: 'bg-orange-500 hover:bg-orange-600',
  },
  default: {
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    confirmBtn: 'bg-[#2563eb] hover:bg-[#1d4ed8]',
  },
};

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
}) => {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.danger;

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={loading ? undefined : onClose}
      contentClassName="sm:max-w-[440px] w-full rounded-xl bg-white overflow-hidden"
      zIndex={60}
    >
      <div className="px-6 pt-6 pb-5">
        <div className="flex items-start gap-4">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}
          >
            <AlertTriangle className={`w-5 h-5 ${styles.iconColor}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 leading-6">{title}</h3>
            {message && (
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{message}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-4">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          data-testid="confirm-modal-btn"
          disabled={loading}
          className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors disabled:opacity-60 ${styles.confirmBtn}`}
        >
          {loading ? 'Please wait…' : confirmLabel}
        </button>
      </div>
    </AnimatedModal>
  );
};

export default ConfirmModal;
